/**
 * Rigid transforms for authored objects. Rotations are stored as intrinsic
 * XYZ Euler degrees (the same convention as SolidDef.rotationDeg and
 * THREE.Euler 'XYZ'); composition happens in quaternions.
 */
import { eulerDegToQuat, type Vec3Tuple } from '../world/LevelData';

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface RigidTransform {
  position: Vec3Tuple;
  rotation: Quat;
}

const RAD = 180 / Math.PI;

export const IDENTITY: RigidTransform = Object.freeze({ position: [0, 0, 0] as Vec3Tuple, rotation: Object.freeze({ x: 0, y: 0, z: 0, w: 1 }) });

export function quatFromEulerDeg(r: Vec3Tuple | undefined): Quat {
  return eulerDegToQuat(r, { x: 0, y: 0, z: 0, w: 1 });
}

/** Hamilton product a ⊗ b (apply b, then a). */
export function quatMul(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

export function quatAxisAngle(axis: Vec3Tuple, deg: number): Quat {
  const h = deg / RAD / 2;
  const s = Math.sin(h);
  return { x: axis[0] * s, y: axis[1] * s, z: axis[2] * s, w: Math.cos(h) };
}

export function rotateVec(q: Quat, v: Vec3Tuple): Vec3Tuple {
  const tx = 2 * (q.y * v[2] - q.z * v[1]);
  const ty = 2 * (q.z * v[0] - q.x * v[2]);
  const tz = 2 * (q.x * v[1] - q.y * v[0]);
  return [v[0] + q.w * tx + (q.y * tz - q.z * ty), v[1] + q.w * ty + (q.z * tx - q.x * tz), v[2] + q.w * tz + (q.x * ty - q.y * tx)];
}

/** Quaternion → intrinsic XYZ Euler degrees (THREE.Euler.setFromRotationMatrix, order 'XYZ'). */
export function quatToEulerDeg(q: Quat): [number, number, number] {
  const { x, y, z, w } = q;
  const m11 = 1 - 2 * (y * y + z * z);
  const m12 = 2 * (x * y - z * w);
  const m13 = 2 * (x * z + y * w);
  const m22 = 1 - 2 * (x * x + z * z);
  const m23 = 2 * (y * z - x * w);
  const m32 = 2 * (y * z + x * w);
  const m33 = 1 - 2 * (x * x + y * y);
  const ry = Math.asin(Math.max(-1, Math.min(1, m13)));
  let rx: number;
  let rz: number;
  if (Math.abs(m13) < 0.9999999) {
    rx = Math.atan2(-m23, m33);
    rz = Math.atan2(-m12, m11);
  } else {
    rx = Math.atan2(m32, m22);
    rz = 0;
  }
  return [rx * RAD, ry * RAD, rz * RAD];
}

/** parent ∘ child: the child's transform expressed in the parent's space. */
export function compose(parent: RigidTransform, child: RigidTransform): RigidTransform {
  const p = rotateVec(parent.rotation, child.position);
  return {
    position: [parent.position[0] + p[0], parent.position[1] + p[1], parent.position[2] + p[2]],
    rotation: quatMul(parent.rotation, child.rotation),
  };
}

/** Heading of the transformed −Z axis, in degrees (0 = −Z, 90 = −X), as SpawnPoint.yawDeg. */
export function yawDegOf(q: Quat): number {
  const f = rotateVec(q, [0, 0, -1]);
  return Math.atan2(-f[0], -f[2]) * RAD;
}

/** Rounds to a fixed grid so equivalent inputs produce identical compiled numbers. */
export function canon(v: number, step = 1e-6): number {
  const r = Math.round(v / step) * step;
  // Normalise −0 and shed binary noise from the multiplication.
  return Object.is(r, -0) ? 0 : Number(r.toFixed(9));
}
